import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import { useAppStore } from "../../../providers/AppStore";
import { useAuth } from "../../../providers/AuthProvider";
import { UserType } from "../../../common/entities/profile";
import { setCustomerSession } from "../../../common/network/customerSession";
import { clearClientTabName, setClientTabName } from "../utils/clientTabs";

/**
 * Whose data is this tab showing?
 *
 * - `self`: the logged-in customer (role 1, /platform routes). The subject is
 *   AuthUser; no customer token is minted and any `?id=` is ignored.
 * - `client`: an affiliate viewing one client in a dedicated browser tab
 *   (/client workspace routes). The subject is resolved from the `?id=` query
 *   param on every id change: the client's token is minted into the tab's
 *   sessionStorage and the workspace data is loaded. One client per tab.
 */
export type SubjectMode = "self" | "client";

export type SubjectStatus = "loading" | "ready" | "error";

export type SubjectErrorKind =
  | "missing_id"
  | "load_failed"
  | "questionnaire_incomplete";

type SubjectContextValue = {
  mode: SubjectMode;
  status: SubjectStatus;
  errorKind: SubjectErrorKind | null;
  errorEmail: string | null;
  subjectId: string | null;
  isSelf: boolean;
  subject: UserType | null;
};

const SubjectContext = createContext<SubjectContextValue | null>(null);

type PropsType = {
  mode: SubjectMode;
  children: ReactNode;
};

export const SubjectProvider = ({ mode, children }: PropsType) => {
  const { AuthUser } = useAuth();
  const {
    ASGetBiomarkersList,
    ASGetLogicCategoriesList,
    ASLoadClientData,
    ASSelectedClient,
    ASSetSelectedClient,
    ASSetIsLoadingPlatformData,
  } = useAppStore();
  const [searchParams] = useSearchParams();

  const subjectId =
    mode === "self" ? AuthUser.pnoe_user_id ?? null : searchParams.get("id");

  // Keep this tab's window.name in step with the client it is showing.
  // `openClientTab` targets tabs by that name, and the browser never updates it
  // on navigation — so without this a tab moved from client A to client B would
  // still answer to A's name and get hijacked by a later click on A.
  useEffect(() => {
    if (mode !== "client") return;
    if (subjectId) {
      setClientTabName(subjectId);
    } else {
      clearClientTabName();
    }
  }, [mode, subjectId]);

  const [status, setStatus] = useState<SubjectStatus>("loading");
  const [errorKind, setErrorKind] = useState<SubjectErrorKind | null>(null);
  const [errorEmail, setErrorEmail] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      setStatus("loading");
      setErrorKind(null);
      setErrorEmail(null);

      if (mode === "self") {
        // The biomarker catalogue is started, not awaited: it is 6 MB (~9 s
        // uncached), nothing needs it before first paint, and its consumers
        // (description tooltips, the About tab) fill in when it lands.
        // Everything else — the report, the customer's own biomarkers,
        // integrations, and both questionnaires — is declared by the page that
        // uses it via `usePlatformResource`, matching the client workspace.
        void ASGetBiomarkersList();
        if (!cancelled) {
          // The flag starts true and, in the client workspace, is cleared by
          // loadClientData. Self mode no longer calls that — the pages declare
          // what they need — so it has to clear it itself or the shell sits on
          // its loader forever.
          ASSetIsLoadingPlatformData(false);
          setStatus("ready");
        }
        return;
      }

      if (!subjectId) {
        ASSetIsLoadingPlatformData(false);
        setErrorKind("missing_id");
        setStatus("error");
        return;
      }

      // Reset any client context this tab may carry (e.g. sessionStorage
      // copied into a duplicated tab) and hydrate from the URL id.
      setCustomerSession(null);
      ASSetSelectedClient(null);
      ASSetIsLoadingPlatformData(true);

      // The biomarker catalogue is started, not awaited (see the self branch).
      void ASGetBiomarkersList();
      await ASGetLogicCategoriesList();

      try {
        const result = await ASLoadClientData(subjectId, AuthUser.role);
        if (cancelled) {
          return;
        }
        if (result.ok) {
          setStatus("ready");
        } else {
          setErrorKind(result.error);
          setErrorEmail(result.email ?? null);
          setStatus("error");
        }
      } catch (error) {
        console.error("Error loading client workspace:", error);
        if (!cancelled) {
          ASSetIsLoadingPlatformData(false);
          setErrorKind("load_failed");
          setStatus("error");
        }
      }
    };

    bootstrap();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, subjectId]);

  const value: SubjectContextValue = {
    mode,
    status,
    errorKind,
    errorEmail,
    subjectId,
    isSelf: mode === "self",
    subject: mode === "self" ? AuthUser : ASSelectedClient,
  };

  return (
    <SubjectContext.Provider value={value}>{children}</SubjectContext.Provider>
  );
};

export const useSubject = () => {
  const context = useContext(SubjectContext);

  if (!context) {
    throw new Error("useSubject must be used inside SubjectProvider");
  }

  return context;
};
